import { useEffect, useState } from "react";
import { Alert, Card, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { apiFetch } from "../../services/apiClient";

type Asignacion = {
  id: number;
  detalleId: number;
  lavanderiaNombre: string;
  fecha: string;
  descripcion: string;
  proveedor?: string | null;
  monto: number;
  acreedor?: { tipo?: string; nombre?: string | null } | null;
  modalidad?: string;
  notas?: string | null;
  estadoPago: string;
  estadoReintegroAf?: string | null;
};

/** Dos etapas distintas: AF paga a la persona y la lavandería reintegra a AF. */
export default function IntermediacionesLavanderia({ desde, hasta }: { desde: string; hasta: string }) {
  const [filas, setFilas] = useState<Asignacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let activo = true;
    let version = 0;
    const cargar = () => {
      const actual = ++version;
      setCargando(true); setError(false);
      const parametros = new URLSearchParams({ desde, hasta });
      void apiFetch<{ data: Asignacion[] }>(`/finanzas/lavanderia-asignaciones/intermediadas?${parametros}`, { errorMsg: "No se pudieron cargar las intermediaciones de lavandería" }).then((respuesta) => {
        if (!activo || actual !== version) return;
        setFilas(respuesta?.data || []); setError(!respuesta); setCargando(false);
      }).catch(() => { if (activo && actual === version) { setFilas([]); setError(true); setCargando(false); } });
    };
    cargar();
    window.addEventListener("focus", cargar);
    return () => { activo = false; window.removeEventListener("focus", cargar); };
  }, [desde, hasta]);
  if (!cargando && !error && filas.length === 0) return null;
  const estadoPersona = (fila: Asignacion) => fila.acreedor?.tipo === "empresa" ? <Tag color="blue">AF pagó al proveedor</Tag> : fila.estadoPago === "pagado" ? <Tag color="green">Pagado a la persona</Tag> : fila.estadoPago === "pendiente" ? <Tag color="gold">Pendiente de pago a la persona</Tag> : <Tag>Estado sin indicar</Tag>;
  const estadoReintegro = (fila: Asignacion) => {
    const estado = fila.acreedor?.tipo === "empresa" ? fila.estadoPago : fila.estadoReintegroAf;
    if (fila.acreedor?.tipo !== "empresa" && fila.estadoPago !== "pagado") return <Typography.Text type="secondary">Se determina al pagar a la persona</Typography.Text>;
    return estado === "pagado" ? <Tag color="green">Reintegrado a AF</Tag> : estado === "pendiente" ? <Tag color="gold">Lavandería debe a AF</Tag> : <Tag>Reintegro sin indicar</Tag>;
  };
  return <Card title="Pagos de AF por lavanderías" style={{ marginTop: 16 }}>
    <Typography.Paragraph type="secondary">AF puede pagar a una persona o directamente a un proveedor. El reintegro de la lavandería a AF se registra por separado. Estos movimientos no se suman a las tarjetas superiores.</Typography.Paragraph>
    {error ? <Alert type="error" showIcon message="No se pudieron cargar las intermediaciones de lavandería" /> : <Table<Asignacion> rowKey="id" loading={cargando} dataSource={filas} pagination={false} size="small" scroll={{ x: 900 }} columns={[
      { title: "Fecha", dataIndex: "fecha", render: (v: string) => dayjs(v).format("DD-MM-YYYY") },
      { title: "Lavandería", dataIndex: "lavanderiaNombre" },
      { title: "Destino", render: (_: unknown, x: Asignacion) => x.acreedor?.tipo === "empresa" ? x.proveedor || "Proveedor sin indicar" : x.acreedor?.nombre || "Sin indicar" },
      { title: "Detalle", dataIndex: "descripcion" },
      { title: "Monto", dataIndex: "monto", align: "right", render: (v: number) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v) },
      { title: "Pago de AF", render: (_: unknown, x: Asignacion) => estadoPersona(x) },
      { title: "Lavandería → AF", render: (_: unknown, x: Asignacion) => estadoReintegro(x) },
    ]} />}
  </Card>;
}
